
import { useState, useEffect, useRef, createContext, useContext } from "react";

const Divider = () => {
    return (
        <div
            style={{
                height: '1px',
                backgroundColor: '#e0e0e0',
                margin: '4px 0',
            }}
        />
    );
}

/**
 * @param {object} props
 * @param {string} props.label
 * @param {(clickedWidgetId: string|null) => void} props.onClick
 * @param {(clickedWidgetId: string|null) => boolean} [props.disabled]
 * @returns
 */
const Button = ({ label, onClick, disabled }) => {

    const { clickedWidgetId, setVisible } = useContext(ContextMenuContext);

    disabled = disabled || (() => false);
    
    const isDisabled = disabled(clickedWidgetId);

    return <button
        onClick={() => {
            if (!isDisabled) {
                onClick(clickedWidgetId);
            }
            setVisible(false);
        }}
        disabled={isDisabled}
        style={{
            width: '100%',
            padding: '8px 16px',
            border: 'none',
            background: 'none',
            textAlign: 'left',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            color: isDisabled ? '#999' : '#333',
            fontSize: '14px',
        }}
        onMouseEnter={(e) => {
            if (!isDisabled) {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
            }
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
        }}
    >
        {label}
    </button>
}

export const ContextMenuContext = createContext({
    clickedWidgetId: null,
    /** @argument {boolean} visible */
    setVisible: (visible) => {},
});

/**
 * Right-click context menu for the editor.
 * @param {object} props
 * @param {React.ReactNode} props.children - Menu items to display
 * @returns {JSX.Element|null}
 */
export const ContextMenu = ({ children }) => {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [clickedWidgetId, setClickedWidgetId] = useState(null);

    /** @type {React.RefObject<HTMLDivElement>} */
    const menuRef = useRef(null);

    useEffect(() => {
        /** @argument {MouseEvent} event */
        const handleContextMenu = (event) => {
            /* Only handle right-clicks within the editor area */
            const canvasArea = document.querySelector(
                '.report-builder-canvas-area'
            );
            
            if (canvasArea && canvasArea.contains(event.target)) {
                event.preventDefault();
                
                /* Find the widget that was clicked */
                const widgetElement = event.target.closest(
                    '[data-id]'
                );
                const widgetId = widgetElement?.getAttribute('data-id') || 
                    null;
                
                setClickedWidgetId(widgetId);
                setPosition({ x: event.clientX, y: event.clientY });
                setVisible(true);
            }
        };

        /** @argument {MouseEvent} event */
        const handleClickOutside = (event) => {
            if (menuRef.current && 
                !menuRef.current.contains(event.target)) {
                setVisible(false);
            }
        };

        /** Handle escape key to close menu */
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setVisible(false);
            }
        };

        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('mousedown',  handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    if (!visible) {
        return null;
    }

    return (
        <div
            ref={menuRef}
            style={{
                position: 'fixed',
                top: position.y,
                left: position.x,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                minWidth: '150px',
                padding: '4px 0',
            }}
        >
            <ContextMenuContext.Provider value={{ clickedWidgetId, setVisible }}>
                {children}
            </ContextMenuContext.Provider>
        </div>
    );
}

/* Export sub-components */
Divider.displayName = 'ContextMenu.Divider';
Button.displayName = 'ContextMenu.Button';

ContextMenu.Divider = Divider;
ContextMenu.Button = Button;
